import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { userSchemas } from '@/api/users/user.schema';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormSelect } from '@/components/ui/form-fields/form-select';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { Icon } from '@/components/ui/icon';
import { userRoleOptions } from '@/db/schema/users';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { useCreateUser } from '@/services/users';
import type { ValidationError } from '@/types/api/validation-error';

export const Route = createFileRoute('/_app/users/create')({
  head: () => meta({ label: 'Novo usuário' }),
  component: RouteComponent,
});

type Schema = import('zod').input<typeof userSchemas.create>;

function RouteComponent() {
  const router = useRouter();
  const { mutate: save, error, isSuccess, isPending: isSubmitting } = useCreateUser();

  const form = useForm<Schema>({
    resolver: zodResolver(userSchemas.create) as any,
    shouldUseNativeValidation: false,
    defaultValues: { role: 'user', isActive: true },
  });

  const handleSubmit = (data: Schema) => save(data as any);

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error?.status === 422) {
      const { errors } = error.value as unknown as ValidationError<Schema>;

      if (errors) {
        for (const [key, message] of Object.entries(errors)) {
          form.setError(key as keyof Schema, { message: message as string });
        }
      }
    }
  }, [error, form]);

  return (
    <Content
      breadcrumbs={{
        icon: 'users',
        title: 'Novo usuário',
        items: [
          { label: 'Listagem', route: '/users' },
          { label: 'Novo', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title="Dados do usuário"
        description="Informe os dados do usuário."
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <FormInput name="name" control={form.control} label="Nome" required />
          <FormInput name="email" control={form.control} label="E-mail" required />
          <FormInput
            type="password"
            name="password"
            control={form.control}
            label="Senha"
            required
          />
          <FormSelect
            name="role"
            control={form.control}
            label="Perfil"
            required
            options={userRoleOptions}
          />
          <FormSwitch name="isActive" control={form.control} label="Ativo" />
        </FormContent>
        <FormFooter>
          <Button
            type="button"
            disabled={isSubmitting}
            variant="outline"
            onClick={() => form.reset()}
          >
            Limpar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="success"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
    </Content>
  );
}
